import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Clock, CurrencyDollar, CheckCircle, XCircle, Bug, Lightbulb, Gear, Database, Wrench, FileText, Eye, Archive, Trash } from "@phosphor-icons/react";
import LineDescription from "@/components/proposals/LineDescription";

const REQUEST_TYPES = [
  { value: "bug_fix", label: "Bug Fix", icon: Bug, color: "text-[#DC2626]" },
  { value: "new_feature", label: "New Feature", icon: Lightbulb, color: "text-[#2563EB]" },
  { value: "enhancement", label: "Enhancement", icon: Wrench, color: "text-[#7C3AED]" },
  { value: "config_change", label: "Configuration Change", icon: Gear, color: "text-[#D97706]" },
  { value: "data_request", label: "Data Request", icon: Database, color: "text-[#0891B2]" },
];

const STATUS_STYLES = {
  pending: { bg: "bg-[#FFFBEB]", text: "text-[#D97706]", label: "Pending Review" },
  in_review: { bg: "bg-[#F0F7FF]", text: "text-[#0055FF]", label: "In Review" },
  estimate_sent: { bg: "bg-[#FEF3C7]", text: "text-[#92400E]", label: "Estimate Sent" },
  accepted_by_eyvo: { bg: "bg-[#F0FDF4]", text: "text-[#16A34A]", label: "Accepted by Eyvo" },
  rejected_by_eyvo: { bg: "bg-[#FEF2F2]", text: "text-[#DC2626]", label: "Rejected by Eyvo" },
  approved: { bg: "bg-[#F0FDF4]", text: "text-[#16A34A]", label: "Accepted by Eyvo" },
  rejected: { bg: "bg-[#FEF2F2]", text: "text-[#DC2626]", label: "Rejected by Eyvo" },
  in_progress: { bg: "bg-[#EFF6FF]", text: "text-[#2563EB]", label: "In Progress" },
  completed: { bg: "bg-[#F0FDF4]", text: "text-[#16A34A]", label: "Completed" },
  archived: { bg: "bg-[#F5F5F5]", text: "text-[#A3A3A3]", label: "Archived" },
};

export default function ChangeRequestPanel({ projectId, user }) {
  const [changeRequests, setChangeRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [reviewCR, setReviewCR] = useState(null);
  const [viewQuote, setViewQuote] = useState(null);
  const [finopsPrompt, setFinopsPrompt] = useState(null);
  const [form, setForm] = useState({ title: "", description: "", request_type: "new_feature", cost_impact: 0, timeline_impact: "" });
  const [reviewForm, setReviewForm] = useState({ confirmed_type: "", is_billable: false, cost_estimate: 0, cost_currency: "USD", estimate_notes: "", status: "in_review" });

  useEffect(() => { loadCRs(); }, [projectId]);

  const loadCRs = async () => {
    try {
      const res = await api.get(`/projects/${projectId}/change-requests`);
      setChangeRequests(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.title || !form.description) return toast.error("Title and description are required");
    try {
      await api.post(`/projects/${projectId}/change-requests`, form);
      toast.success("Change request submitted");
      setShowCreate(false);
      setForm({ title: "", description: "", request_type: "new_feature", cost_impact: 0, timeline_impact: "" });
      loadCRs();
    } catch (err) { toast.error("Failed to create change request"); }
  };

  const handleReviewSubmit = async () => {
    if (!reviewCR) return;
    try {
      const payload = { ...reviewForm };
      if (payload.is_billable && payload.cost_estimate > 0) {
        payload.status = "estimate_sent";
      }
      await api.put(`/change-requests/${reviewCR.id}`, payload);
      toast.success(payload.is_billable ? "Estimate sent to client" : "Review saved");
      setReviewCR(null);
      loadCRs();
    } catch (err) { toast.error("Failed to update"); }
  };

  const handleStatusUpdate = async (crId, newStatus) => {
    try {
      await api.put(`/change-requests/${crId}`, { status: newStatus });
      const label = STATUS_STYLES[newStatus]?.label || newStatus.replace(/_/g, " ");
      toast.success(`Status updated to ${label}`);
      loadCRs();
    } catch (err) { toast.error("Failed to update"); }
  };

  const handleArchiveCR = async (crId) => {
    try {
      await api.put(`/change-requests/${crId}/archive`);
      toast.success("Change request archived");
      loadCRs();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to archive"); }
  };

  const handleDeleteCR = async (crId, title) => {
    if (!window.confirm(`Permanently delete "${title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/change-requests/${crId}`);
      toast.success("Change request deleted");
      loadCRs();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to delete"); }
  };

  const handleClientDecision = async (crId, decision) => {
    if (decision === "accepted") {
      try {
        const checkRes = await api.get(`/projects/${projectId}/finops-client-check`);
        if (!checkRes.data.exists) {
          setFinopsPrompt({ crId, tenantName: checkRes.data.tenant_name });
          return;
        }
      } catch (err) {
        console.error("FinOps check failed, proceeding anyway", err);
      }
    }
    await submitClientDecision(crId, decision);
  };

  const handleFinopsCreate = async (createClient) => {
    if (!finopsPrompt) return;
    const { crId } = finopsPrompt;
    if (createClient) {
      try {
        await api.post(`/projects/${projectId}/finops-client-create`);
        toast.success(`Client "${finopsPrompt.tenantName}" created in Financial Ops`);
      } catch (err) {
        toast.error("Failed to create client in Financial Ops");
        setFinopsPrompt(null);
        return;
      }
    }
    setFinopsPrompt(null);
    await submitClientDecision(crId, "accepted");
  };

  const submitClientDecision = async (crId, decision) => {
    try {
      const res = await api.put(`/change-requests/${crId}/client-decision`, { decision });
      toast.success(decision === "accepted" ? "Estimate accepted" : "Estimate rejected");
      if (res.data.finops_quote_number) {
        toast.success(`Proposal ${res.data.finops_quote_number} created`);
      }
      loadCRs();
    } catch (err) { toast.error("Failed to submit decision"); }
  };

  const handleViewQuote = async (crId) => {
    try {
      const res = await api.get(`/change-requests/${crId}/quote`);
      setViewQuote(res.data);
    } catch (err) { toast.error("Quote not found"); }
  };

  const openReview = (cr) => {
    setReviewCR(cr);
    setReviewForm({
      confirmed_type: cr.confirmed_type || cr.request_type || "new_feature",
      is_billable: !!cr.is_billable,
      cost_estimate: cr.cost_estimate || 0,
      cost_currency: cr.cost_currency || "USD",
      estimate_notes: cr.estimate_notes || "",
      status: cr.status || "in_review",
    });
  };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-6 h-6 border-2 border-[#0055FF] border-t-transparent animate-spin" /></div>;

  const isInternal = user?.user_type === "internal";

  return (
    <div data-testid="change-request-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">Change Requests ({changeRequests.length})</h3>
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogTrigger asChild>
            <Button className="bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-9 px-4 text-xs" data-testid="create-cr-button"><Plus size={14} weight="bold" className="mr-1.5" />New Request</Button>
          </DialogTrigger>
          <DialogContent className="rounded-none border-[#E5E5E5] max-w-md">
            <DialogHeader><DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Submit Change Request</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Request Type</Label>
                <Select value={form.request_type} onValueChange={v => setForm({...form, request_type: v})}>
                  <SelectTrigger className="rounded-none border-[#E5E5E5] h-10" data-testid="cr-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    {REQUEST_TYPES.map(rt => <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Title</Label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" placeholder="Brief summary" data-testid="cr-title-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Description</Label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="rounded-none border-[#E5E5E5] min-h-[100px]" placeholder="Describe the change in detail" data-testid="cr-description-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Timeline Impact</Label>
                <Input value={form.timeline_impact} onChange={e => setForm({...form, timeline_impact: e.target.value})} className="rounded-none border-[#E5E5E5] h-10" placeholder="e.g. +2 weeks" data-testid="cr-timeline-input" />
              </div>
              <Button onClick={handleCreate} className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-10" data-testid="submit-cr-button">Submit Request</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-3">
        {changeRequests.map((cr) => {
          const st = STATUS_STYLES[cr.status] || STATUS_STYLES.pending;
          const typeInfo = REQUEST_TYPES.find(t => t.value === (cr.confirmed_type || cr.request_type)) || REQUEST_TYPES[1];
          const TypeIcon = typeInfo.icon;
          return (
            <Card key={cr.id} className="border border-[#E5E5E5] rounded-none p-4" data-testid={`cr-card-${cr.id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <TypeIcon size={16} className={typeInfo.color} weight="bold" />
                  <h4 className="font-bold text-[#0A0A0A] text-sm">{cr.title}</h4>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge className={`${st.bg} ${st.text} border-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-none`}>{st.label}</Badge>
                  <Badge className="bg-[#F5F5F5] text-[#525252] border-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-none">{typeInfo.label}</Badge>
                </div>
              </div>

              <p className="text-xs text-[#525252] mb-3 line-clamp-2">{cr.description}</p>

              <div className="flex items-center gap-4 text-xs text-[#A3A3A3] mb-3 flex-wrap">
                {cr.timeline_impact && <span className="flex items-center gap-1"><Clock size={12} />{cr.timeline_impact}</span>}
                {cr.submitted_by_name && <span>By {cr.submitted_by_name}</span>}
                {cr.reviewed_by_name && <span>Reviewed by {cr.reviewed_by_name}</span>}
              </div>

              {/* Billable estimate section */}
              {cr.is_billable && cr.cost_estimate > 0 && (
                <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-3 mb-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Cost Estimate</p>
                      <p className="text-lg font-black text-[#0A0A0A]">{cr.cost_currency || "USD"} {cr.cost_estimate?.toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                    </div>
                    {cr.client_decision && (
                      <Badge className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-none border-0 ${cr.client_decision === "accepted" ? "bg-[#F0FDF4] text-[#16A34A]" : "bg-[#FEF2F2] text-[#DC2626]"}`}>
                        {cr.client_decision}
                      </Badge>
                    )}
                  </div>
                  {cr.estimate_notes && <p className="text-xs text-[#525252] mt-1">{cr.estimate_notes}</p>}
                  {cr.finops_quote_number && (
                    <div className="mt-2 flex items-center gap-2">
                      <Badge className="bg-[#F0F7FF] text-[#0055FF] border-0 text-[10px] font-bold px-2 py-0.5 rounded-none">
                        <FileText size={10} className="mr-1 inline" />Proposal #{cr.finops_quote_number}
                      </Badge>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] text-[#0055FF] hover:bg-[#F0F7FF] rounded-none" onClick={() => handleViewQuote(cr.id)} data-testid={`view-quote-${cr.id}`}>
                        <Eye size={12} className="mr-1" />View
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-2 flex-wrap">
                {/* Eyvo: Review pending requests */}
                {isInternal && (cr.status === "pending" || cr.status === "in_review") && (
                  <Button size="sm" onClick={() => openReview(cr)} className="bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-8 px-3 text-xs" data-testid={`cr-review-${cr.id}`}>
                    <Gear size={12} className="mr-1" />Review &amp; Estimate
                  </Button>
                )}

                {/* Eyvo: Accept / Reject by Eyvo */}
                {isInternal && cr.status === "pending" && (
                  <>
                    <Button size="sm" onClick={() => handleStatusUpdate(cr.id, "accepted_by_eyvo")} className="bg-[#16A34A] hover:bg-[#15803D] text-white font-bold rounded-none h-8 px-3 text-xs" data-testid={`cr-accept-eyvo-${cr.id}`}>
                      <CheckCircle size={12} className="mr-1" />Accept
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleStatusUpdate(cr.id, "rejected_by_eyvo")} className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] font-bold h-8 px-3 text-xs" data-testid={`cr-reject-eyvo-${cr.id}`}>
                      <XCircle size={12} className="mr-1" />Reject
                    </Button>
                  </>
                )}

                {/* Client: Accept/Reject estimate */}
                {!isInternal && cr.status === "estimate_sent" && !cr.client_decision && (
                  <>
                    <Button size="sm" onClick={() => handleClientDecision(cr.id, "accepted")} className="bg-[#16A34A] hover:bg-[#15803D] text-white font-bold rounded-none h-8 px-3 text-xs" data-testid={`cr-accept-estimate-${cr.id}`}>
                      <CheckCircle size={12} className="mr-1" />Accept Estimate
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleClientDecision(cr.id, "rejected")} className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] font-bold h-8 px-3 text-xs" data-testid={`cr-reject-estimate-${cr.id}`}>
                      <XCircle size={12} className="mr-1" />Reject Estimate
                    </Button>
                  </>
                )}

                {/* Eyvo: Move accepted to in_progress */}
                {isInternal && (cr.status === "accepted_by_eyvo" || cr.status === "approved") && (
                  <Button size="sm" onClick={() => handleStatusUpdate(cr.id, "in_progress")} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold rounded-none h-8 px-3 text-xs">Start Work</Button>
                )}
                {isInternal && cr.status === "in_progress" && (
                  <Button size="sm" onClick={() => handleStatusUpdate(cr.id, "completed")} className="bg-[#16A34A] hover:bg-[#15803D] text-white font-bold rounded-none h-8 px-3 text-xs">Mark Complete</Button>
                )}

                {/* Eyvo: Archive / Delete */}
                {isInternal && cr.status !== "archived" && (
                  <Button size="sm" variant="outline" onClick={() => handleArchiveCR(cr.id)} className="rounded-none border-[#E5E5E5] text-[#525252] font-bold h-8 px-3 text-xs" data-testid={`cr-archive-${cr.id}`}>
                    <Archive size={12} className="mr-1" />Archive
                  </Button>
                )}
                {isInternal && (
                  <Button size="sm" variant="outline" onClick={() => handleDeleteCR(cr.id, cr.title)} className="rounded-none border-[#DC2626] text-[#DC2626] hover:bg-[#FEF2F2] font-bold h-8 px-3 text-xs" data-testid={`cr-delete-${cr.id}`}>
                    <Trash size={12} className="mr-1" />Delete
                  </Button>
                )}
              </div>
            </Card>
          );
        })}
        {changeRequests.length === 0 && <div className="text-center py-12 text-[#A3A3A3]"><p className="font-medium">No change requests yet</p></div>}
      </div>

      {/* Review Dialog (Eyvo internal) */}
      <Dialog open={!!reviewCR} onOpenChange={(open) => { if (!open) setReviewCR(null); }}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-md">
          <DialogHeader><DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Review: {reviewCR?.title}</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="bg-[#F5F5F5] p-3 text-xs text-[#525252]">{reviewCR?.description}</div>
            <div className="space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Confirm Request Type</Label>
              <Select value={reviewForm.confirmed_type} onValueChange={v => setReviewForm({...reviewForm, confirmed_type: v})}>
                <SelectTrigger className="rounded-none border-[#E5E5E5] h-10"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">{REQUEST_TYPES.map(rt => <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={reviewForm.is_billable} onChange={e => setReviewForm({...reviewForm, is_billable: e.target.checked})} className="h-4 w-4 rounded-none" data-testid="cr-billable-checkbox" />
                <span className="text-sm font-medium text-[#0A0A0A]">This is a billable request</span>
              </label>
            </div>
            {reviewForm.is_billable && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Cost Estimate</Label>
                    <Input type="number" value={reviewForm.cost_estimate} onChange={e => setReviewForm({...reviewForm, cost_estimate: parseFloat(e.target.value) || 0})} className="rounded-none border-[#E5E5E5] h-10" data-testid="cr-estimate-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Currency</Label>
                    <Select value={reviewForm.cost_currency} onValueChange={v => setReviewForm({...reviewForm, cost_currency: v})}>
                      <SelectTrigger className="rounded-none border-[#E5E5E5] h-10"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none">
                        <SelectItem value="USD">USD</SelectItem>
                        <SelectItem value="GBP">GBP</SelectItem>
                        <SelectItem value="EUR">EUR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-bold uppercase tracking-[0.15em] text-[#525252]">Notes for Client</Label>
                  <Textarea value={reviewForm.estimate_notes} onChange={e => setReviewForm({...reviewForm, estimate_notes: e.target.value})} className="rounded-none border-[#E5E5E5] min-h-[60px]" placeholder="e.g. Breakdown of hours..." />
                </div>
              </>
            )}
            <Button onClick={handleReviewSubmit} className="w-full bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-10" data-testid="submit-review-button">
              {reviewForm.is_billable ? "Send Estimate to Client" : "Save Review"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Quote Dialog */}
      <Dialog open={!!viewQuote} onOpenChange={(open) => { if (!open) setViewQuote(null); }}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-lg">
          <DialogHeader><DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Proposal #{viewQuote?.quote_number}</DialogTitle></DialogHeader>
          {viewQuote && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Client</p><p className="font-medium text-[#0A0A0A]">{viewQuote.recipient_name}</p></div>
                <div><p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Status</p><Badge className="bg-[#F0FDF4] text-[#16A34A] border-0 text-[10px] font-bold uppercase px-2 py-0.5 rounded-none">{viewQuote.status}</Badge></div>
                <div><p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Date</p><p className="text-[#525252]">{viewQuote.quote_date}</p></div>
                <div><p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Reference</p><p className="text-[#525252]">{viewQuote.reference_number}</p></div>
              </div>
              <div className="border border-[#E5E5E5]">
                <div className="bg-[#F5F5F5] px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Line Items</div>
                {viewQuote.line_items?.map((li, i) => (
                  <div key={i} className="px-4 py-3 border-t border-[#F5F5F5] flex items-start justify-between">
                    <LineDescription text={li.description_snapshot} className="text-sm text-[#0A0A0A] pr-4" />
                    <span className="text-sm font-bold text-[#0A0A0A] whitespace-nowrap">{viewQuote.currency_code} {li.line_subtotal_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                ))}
                <div className="px-4 py-3 border-t-2 border-[#0A0A0A] flex justify-between bg-[#FAFAFA]">
                  <span className="text-sm font-bold text-[#0A0A0A]">Total</span>
                  <span className="text-lg font-black text-[#0A0A0A]">{viewQuote.currency_code} {viewQuote.total_amount?.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                </div>
              </div>
              {viewQuote.notes_external && <div className="bg-[#F5F5F5] p-3 text-xs text-[#525252]"><p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3] mb-1">Notes</p>{viewQuote.notes_external}</div>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* FinOps Client Not Found Dialog */}
      <Dialog open={!!finopsPrompt} onOpenChange={(open) => { if (!open) setFinopsPrompt(null); }}>
        <DialogContent className="rounded-none border-[#E5E5E5] max-w-md" data-testid="finops-client-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Client Not Found in Financial Ops</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-[#525252]">
              <span className="font-bold text-[#0A0A0A]">"{finopsPrompt?.tenantName}"</span> doesn't exist in Financial Ops. Would you like to create it?
            </p>
            <div className="flex gap-3">
              <Button onClick={() => handleFinopsCreate(true)} className="flex-1 bg-[#0055FF] hover:bg-[#0040CC] text-white font-bold rounded-none h-10" data-testid="finops-create-yes">
                Yes, Create Client
              </Button>
              <Button variant="outline" onClick={() => handleFinopsCreate(false)} className="flex-1 rounded-none h-10 border-[#E5E5E5] font-bold" data-testid="finops-create-no">
                No, Continue Without
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
